import React, { useState, useRef, useEffect } from 'react';
import { Card, CardBody, Button, Image, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Select, SelectItem, addToast } from '@heroui/react';
import { 
  UserIcon,
  CalendarIcon,
  HeartIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowUpTrayIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { uploadUserProfilePicture, prescribeExerciseToUser, fetchExcercises, revokePrescribedExerciseFromUser, reorderUserPrescriptions } from '../../services/api';

const UserDetail = ({ user, onUserUpdated }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  
  // Prescription modal states
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [selectedExerciseId, setSelectedExerciseId] = useState('');
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [prescriptionForm, setPrescriptionForm] = useState({
    repetition: '',
    holdTime: '',
    sets: '',
    restDuration: ''
  });
  const [isPrescribing, setIsPrescribing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [revokingExerciseId, setRevokingExerciseId] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [exerciseToRevoke, setExerciseToRevoke] = useState(null);
  const [draggedExerciseId, setDraggedExerciseId] = useState(null);
  const [dragOverExerciseId, setDragOverExerciseId] = useState(null);
  const [videoPanel1, setVideoPanel1] = useState(null);
  const [videoPanel2, setVideoPanel2] = useState(null);
  const [videoPanel1Offset, setVideoPanel1Offset] = useState({ x: 0, y: 0 });
  const [videoPanel2Offset, setVideoPanel2Offset] = useState({ x: 0, y: 0 });

  const videoDragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moveHandler: null,
    upHandler: null
  });

  const videoInfoDragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moveHandler: null,
    upHandler: null
  });

  const clampDragOffset = (x, y) => ({
    x: Math.max(-500, Math.min(500, x)),
    y: Math.max(-300, Math.min(300, y))
  });

  const startDraggingModal = (event, dragRef, currentOffset, setOffset) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    const dragState = dragRef.current;
    dragState.isDragging = true;
    dragState.startX = event.clientX;
    dragState.startY = event.clientY;
    dragState.originX = currentOffset.x;
    dragState.originY = currentOffset.y;

    dragState.moveHandler = (moveEvent) => {
      if (!dragState.isDragging) {
        return;
      }

      const deltaX = moveEvent.clientX - dragState.startX;
      const deltaY = moveEvent.clientY - dragState.startY;
      setOffset(clampDragOffset(dragState.originX + deltaX, dragState.originY + deltaY));
    };

    dragState.upHandler = () => {
      dragState.isDragging = false;
      if (dragState.moveHandler) {
        window.removeEventListener('mousemove', dragState.moveHandler);
      }
      if (dragState.upHandler) {
        window.removeEventListener('mouseup', dragState.upHandler);
      }
      dragState.moveHandler = null;
      dragState.upHandler = null;
    };

    window.addEventListener('mousemove', dragState.moveHandler);
    window.addEventListener('mouseup', dragState.upHandler);
  };

  const resetModalDragPosition = (setOffset) => {
    setOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    return () => {
      const removeDragListeners = (dragRef) => {
        const dragState = dragRef.current;
        if (dragState.moveHandler) {
          window.removeEventListener('mousemove', dragState.moveHandler);
        }
        if (dragState.upHandler) {
          window.removeEventListener('mouseup', dragState.upHandler);
        }
      };

      removeDragListeners(videoDragRef);
      removeDragListeners(videoInfoDragRef);
    };
  }, []);

  // Load exercises when component mounts
  useEffect(() => {
    const loadExercises = async () => {
      try {
        const response = await fetchExcercises();
        setExercises(response.data || []);
      } catch (error) {
        console.error('Failed to load exercises:', error);
      }
    };
    loadExercises();
  }, []);

  if (!user) {
    return (
      <div className="user-detail-container">
        <div className="empty-state">
          <UserIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-500 mb-2">No User Selected</h3>
          <p className="text-gray-400">Select a user from the list to view their details</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      addToast({
        title: "Invalid File",
        description: "Please select an image file",
        color: "warning"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      addToast({
        title: "File Too Large",
        description: "Image size should be less than 5MB",
        color: "warning"
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setIsUploading(true);
    try {
      const response = await uploadUserProfilePicture(user.id, file);
      console.log('Profile picture uploaded successfully:', response.data);
      setIsUploading(false);
      
      // Update the user with new profile picture path
      if (response.data && response.data.profilePicturePath) {
        const updatedUser = {
          ...user,
          profilePicturePath: response.data.profilePicturePath
        };
        
        // Call parent callback to update the user
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setPreviewImage(null);
      addToast({
        title: "Upload Error",
        description: "Failed to upload profile picture. Please try again.",
        color: "danger"
      });
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const getOrderedPrescriptions = () => {
    return (user.prescription || [])
      .slice()
      .sort((left, right) => {
        const leftOrder = Number.isInteger(left.order) ? left.order : Number.MAX_SAFE_INTEGER;
        const rightOrder = Number.isInteger(right.order) ? right.order : Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return left.exerciseId - right.exerciseId;
      });
  };

  const getPrescriptionListFromResponse = (responseData, fallbackPrescription = []) => {
    if (Array.isArray(responseData)) {
      return responseData;
    }
    return responseData?.prescription || fallbackPrescription;
  };

  const getExerciseTitle = (exerciseId) => {
    const exercise = exercises.find((item) => item.id === exerciseId);
    return exercise?.title || `Exercise ${exerciseId}`;
  };

  const openPrescriptionVideoModal = (exerciseId) => {
    if (isReordering || draggedExerciseId !== null) {
      return;
    }

    const exercise = exercises.find((item) => item.id === exerciseId);
    const panelData = {
      exerciseId,
      title: getExerciseTitle(exerciseId),
      previewVideoPath: exercise?.previewVideoPath || null
    };

    if (!videoPanel1) {
      setVideoPanel1Offset({ x: 0, y: 0 });
      setVideoPanel1(panelData);
    } else {
      setVideoPanel2Offset({ x: 40, y: 40 });
      setVideoPanel2(panelData);
    }
  };

  // Handle prescription form changes
  const handlePrescriptionFormChange = (field, value) => {
    setPrescriptionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle add prescription
  const handleAddPrescription = async () => {
    if (!selectedExerciseId || !prescriptionForm.repetition || !prescriptionForm.holdTime || prescriptionForm.sets === '' || prescriptionForm.restDuration === '') {
      addToast({
        title: "Validation Error",
        description: "Please fill in all prescription fields",
        color: "danger"
      });
      return;
    }

    // Check for duplicate exerciseId
    const exerciseId = parseInt(selectedExerciseId);
    const existingPrescription = user.prescription?.find(p => p.exerciseId === exerciseId);
    if (existingPrescription) {
      addToast({
        title: "Duplicate Exercise",
        description: `Exercise ID ${exerciseId} is already prescribed to this user. Please select a different exercise or edit the existing one.`,
        color: "warning"
      });
      return;
    }

    setIsPrescribing(true);
    try {
      const prescriptionData = {
        repetition: parseInt(prescriptionForm.repetition, 10),
        holdTime: parseFloat(prescriptionForm.holdTime),
        sets: parseInt(prescriptionForm.sets, 10),
        restDuration: parseFloat(prescriptionForm.restDuration)
      };

      const response = await prescribeExerciseToUser(user.id, selectedExerciseId, prescriptionData);
      console.log('Prescription added successfully:', response.data);
      
      // Update the user with new prescription
      if (response.data) {
        // Use prescription from response if available, otherwise create new prescription
        const fallbackPrescription = [...getOrderedPrescriptions(), {
          exerciseId: parseInt(selectedExerciseId, 10),
          repetition: prescriptionData.repetition,
          holdTime: prescriptionData.holdTime,
          sets: prescriptionData.sets,
          restDuration: prescriptionData.restDuration,
          order: (user.prescription || []).length
        }];
        const newPrescription = getPrescriptionListFromResponse(response.data, fallbackPrescription);
        
        const updatedUser = {
          ...user,
          prescription: newPrescription
        };
        
        console.log('Updated user with new prescription:', updatedUser);
        
        // Call parent callback to update the user
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
      } else {
        // Fallback: create prescription locally if response doesn't have prescription data
        const newPrescription = [...getOrderedPrescriptions(), {
          exerciseId: parseInt(selectedExerciseId, 10),
          repetition: prescriptionData.repetition,
          holdTime: prescriptionData.holdTime,
          sets: prescriptionData.sets,
          restDuration: prescriptionData.restDuration,
          order: (user.prescription || []).length
        }];
        
        const updatedUser = {
          ...user,
          prescription: newPrescription
        };
        
        console.log('Fallback: Updated user with new prescription:', updatedUser);
        
        // Call parent callback to update the user
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
      }

      // Show success notification
      addToast({
        title: "Prescription Added",
        description: "Exercise prescription added successfully!",
        color: "success"
      });

      // Reset form and close modal
      setPrescriptionForm({ repetition: '', holdTime: '', sets: '', restDuration: '' });
      setSelectedExerciseId('');
      setIsPrescriptionModalOpen(false);
      
    } catch (error) {
      console.error('Failed to add prescription:', error);
      addToast({
        title: "Add Error",
        description: "Failed to add prescription. Please try again.",
        color: "danger"
      });
    } finally {
      setIsPrescribing(false);
    }
  };

  // Open prescription modal
  const openPrescriptionModal = () => {
    setPrescriptionForm({ repetition: '', holdTime: '', sets: '', restDuration: '' });
    setSelectedExerciseId('');
    setIsPrescriptionModalOpen(true);
  };

  // Open edit prescription modal
  const openEditModal = (exerciseId) => {
    const prescription = user.prescription?.find(p => p.exerciseId === exerciseId);
    if (prescription) {
      setEditingExerciseId(exerciseId);
      setPrescriptionForm({
        repetition: prescription.repetition?.toString() || '',
        holdTime: prescription.holdTime?.toString() || '',
        sets: prescription.sets?.toString() || '1',
        restDuration: prescription.restDuration?.toString() || '0'
      });
      setIsEditModalOpen(true);
    }
  };

  // Handle edit prescription
  const handleEditPrescription = async () => {
    if (!prescriptionForm.repetition || !prescriptionForm.holdTime || prescriptionForm.sets === '' || prescriptionForm.restDuration === '') {
      addToast({
        title: "Validation Error",
        description: "Please fill in all prescription fields",
        color: "danger"
      });
      return;
    }

    setIsUpdating(true);
    try {
      const prescriptionData = {
        repetition: parseInt(prescriptionForm.repetition, 10),
        holdTime: parseFloat(prescriptionForm.holdTime),
        sets: parseInt(prescriptionForm.sets, 10),
        restDuration: parseFloat(prescriptionForm.restDuration)
      };

      const response = await prescribeExerciseToUser(user.id, editingExerciseId, prescriptionData);
      console.log('Prescription updated successfully:', response.data);
      
      // Update the user with new prescription
      if (response.data) {
        const fallbackPrescription = getOrderedPrescriptions().map(p => 
          p.exerciseId === editingExerciseId 
            ? { ...p, repetition: prescriptionData.repetition, holdTime: prescriptionData.holdTime, sets: prescriptionData.sets, restDuration: prescriptionData.restDuration }
            : p
        );
        const newPrescription = getPrescriptionListFromResponse(response.data, fallbackPrescription);
        
        const updatedUser = {
          ...user,
          prescription: newPrescription
        };
        
        // Call parent callback to update the user
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
      }

      // Show success notification
      addToast({
        title: "Prescription Updated",
        description: "Exercise prescription updated successfully!",
        color: "success"
      });

      // Reset form and close modal
      setPrescriptionForm({ repetition: '', holdTime: '', sets: '', restDuration: '' });
      setEditingExerciseId(null);
      setIsEditModalOpen(false);
      
    } catch (error) {
      console.error('Failed to update prescription:', error);
      addToast({
        title: "Update Error",
        description: "Failed to update prescription. Please try again.",
        color: "danger"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Open confirmation modal for revoke prescription
  const openRevokeConfirmModal = (exerciseId) => {
    setExerciseToRevoke(exerciseId);
    setIsConfirmModalOpen(true);
  };

  // Handle revoke prescription
  const handleRevokePrescription = async () => {
    if (!exerciseToRevoke) return;

    setRevokingExerciseId(exerciseToRevoke);
    setIsConfirmModalOpen(false);
    
    try {
      const response = await revokePrescribedExerciseFromUser(user.id, exerciseToRevoke);
      console.log('Prescription revoked successfully:', response.data);
      
      // Update the user with updated prescription list
      if (response.data) {
        const fallbackPrescription = getOrderedPrescriptions()
          .filter(p => p.exerciseId !== exerciseToRevoke)
          .map((prescription, index) => ({
            ...prescription,
            order: index
          }));
        const updatedUser = {
          ...user,
          prescription: getPrescriptionListFromResponse(response.data, fallbackPrescription)
        };
        
        console.log('Updated user after revoking prescription:', updatedUser);
        
        // Call parent callback to update the user
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
      } else {
        // Fallback: remove prescription locally
        const updatedUser = {
          ...user,
          prescription: getOrderedPrescriptions()
            .filter(p => p.exerciseId !== exerciseToRevoke)
            .map((prescription, index) => ({
              ...prescription,
              order: index
            }))
        };
        
        console.log('Fallback: Updated user after revoking prescription:', updatedUser);
        
        // Call parent callback to update the user
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
      }

      // Show success notification
      addToast({
        title: "Prescription Removed",
        description: "Exercise prescription removed successfully!",
        color: "success"
      });
      
    } catch (error) {
      console.error('Failed to revoke prescription:', error);
      addToast({
        title: "Remove Error",
        description: "Failed to remove prescription. Please try again.",
        color: "danger"
      });
    } finally {
      setRevokingExerciseId(null);
      setExerciseToRevoke(null);
    }
  };

  const persistPrescriptionOrder = async (sourceExerciseId, targetExerciseId) => {
    if (sourceExerciseId === targetExerciseId) {
      return;
    }

    const orderedPrescription = getOrderedPrescriptions();
    const sourceIndex = orderedPrescription.findIndex((prescription) => prescription.exerciseId === sourceExerciseId);
    const targetIndex = orderedPrescription.findIndex((prescription) => prescription.exerciseId === targetExerciseId);

    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }

    const reorderedPrescription = orderedPrescription.slice();
    const [movedPrescription] = reorderedPrescription.splice(sourceIndex, 1);
    reorderedPrescription.splice(targetIndex, 0, movedPrescription);

    const optimisticPrescription = reorderedPrescription.map((prescription, index) => ({
      ...prescription,
      order: index
    }));

    setIsReordering(true);
    if (onUserUpdated) {
      onUserUpdated({
        ...user,
        prescription: optimisticPrescription
      });
    }

    try {
      const response = await reorderUserPrescriptions(
        user.id,
        optimisticPrescription.map((prescription) => prescription.exerciseId)
      );
      const persistedPrescription = getPrescriptionListFromResponse(response.data, optimisticPrescription);

      if (onUserUpdated) {
        onUserUpdated({
          ...user,
          prescription: persistedPrescription
        });
      }
    } catch (error) {
      console.error('Failed to reorder prescription:', error);
      if (onUserUpdated) {
        onUserUpdated({
          ...user,
          prescription: orderedPrescription
        });
      }
      addToast({
        title: "Reorder Error",
        description: "Failed to save prescription order. Please try again.",
        color: "danger"
      });
    } finally {
      setIsReordering(false);
      setDraggedExerciseId(null);
      setDragOverExerciseId(null);
    }
  };

  const handlePrescriptionDragStart = (exerciseId) => {
    if (isReordering || revokingExerciseId !== null || isUpdating) {
      return;
    }
    setDraggedExerciseId(exerciseId);
  };

  const handlePrescriptionDragOver = (event, exerciseId) => {
    if (!draggedExerciseId || draggedExerciseId === exerciseId) {
      return;
    }
    event.preventDefault();
    setDragOverExerciseId(exerciseId);
  };

  const handlePrescriptionDrop = async (event, exerciseId) => {
    event.preventDefault();
    if (!draggedExerciseId) {
      return;
    }
    await persistPrescriptionOrder(draggedExerciseId, exerciseId);
  };

  const handlePrescriptionDragEnd = () => {
    setDraggedExerciseId(null);
    setDragOverExerciseId(null);
  };


  return (
    <div className="user-detail-container">
      <div className="user-detail-header">
        <div className="flex items-center gap-4 mb-6">
          <div className="relative">
            {user.profilePicturePath || previewImage ? (
              <div className="w-16 h-16 rounded-full overflow-hidden shadow-lg border-2 border-blue-300">
                <Image
                  alt="Profile picture"
                  className="w-full h-full object-cover"
                  src={previewImage || user.profilePicturePath}
                />
              </div>
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full flex items-center justify-center shadow-lg border-2 border-blue-300">
                <span className="text-blue-800 text-2xl font-bold">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            )}
            
            {/* Upload Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 rounded-full flex items-center justify-center group">
              <div 
                className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={handleButtonClick}
              >
                <div className="bg-white rounded-lg p-2 shadow-lg">
                  {isUploading ? (
                    <div className="flex items-center gap-2 text-gray-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-sm">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-600">
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      <span className="text-sm">Upload</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {user.displayName || user.name || 'Unnamed User'}
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              User ID: {user.id} | External ID: {user.externalId}
            </p>
          </div>
        </div>
      </div>

      <div className="user-detail-content space-y-6">
        {/* Basic Information */}
        <Card className="bg-white dark:bg-gray-800 shadow-sm">
          <CardBody className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <UserIcon className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Basic Information
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <UserIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Username</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.name || 'Not provided'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">External ID</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.externalId || 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <DocumentTextIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Prescriptions</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.prescription?.length || 0} items
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <HeartIcon className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Profile Picture</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.profilePicturePath ? 'Available' : 'Not set'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Prescriptions Information */}
        <Card className="bg-white dark:bg-gray-800 shadow-sm">
          <CardBody className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Prescriptions
                </h3>
              </div>
              <Button
                color="primary"
                size="sm"
                startContent={<PlusIcon className="h-4 w-4" />}
                onPress={openPrescriptionModal}
              >
                Add Prescription
              </Button>
            </div>
            
            <div className="space-y-4">
              {user.prescription && user.prescription.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Active Prescriptions ({user.prescription.length})
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Drag items to reorder
                    </p>
                  </div>
                  <div className="space-y-2">
                    {getOrderedPrescriptions().map((prescription) => (
                      <div
                        key={prescription.exerciseId}
                        draggable={!isReordering && revokingExerciseId === null && !isUpdating}
                        onDragStart={() => handlePrescriptionDragStart(prescription.exerciseId)}
                        onDragOver={(event) => handlePrescriptionDragOver(event, prescription.exerciseId)}
                        onDrop={(event) => handlePrescriptionDrop(event, prescription.exerciseId)}
                        onDragEnd={handlePrescriptionDragEnd}
                        className={`bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border transition-all ${dragOverExerciseId === prescription.exerciseId ? 'border-blue-400 ring-2 ring-blue-200 dark:ring-blue-700' : 'border-green-200 dark:border-green-800'} ${draggedExerciseId === prescription.exerciseId ? 'opacity-60' : ''} ${isReordering ? 'cursor-wait' : 'cursor-move'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="mr-3 flex items-center text-green-700 dark:text-green-300">
                            <Bars3Icon className="h-5 w-5" />
                          </div>
                          <div
                            className="flex-1 cursor-pointer"
                            role="button"
                            tabIndex={0}
                            onClick={() => openPrescriptionVideoModal(prescription.exerciseId)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openPrescriptionVideoModal(prescription.exerciseId);
                              }
                            }}
                            title="Click to preview exercise video"
                          >
                            <p className="text-sm font-medium text-green-800 dark:text-green-200">
                              {getExerciseTitle(prescription.exerciseId)}
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-300">
                              Exercise ID: {prescription.exerciseId} | Repetition: {prescription.repetition} | Hold: {prescription.holdTime}s | Sets: {prescription.sets ?? 1} | Rest: {prescription.restDuration ?? 0}s
                            </p>
                            <p className="text-[11px] text-green-500 dark:text-green-400 mt-1">
                              Click to preview video
                            </p>
                          </div>
                          <div className="ml-4 flex items-center gap-2">
                            <Button
                              isIconOnly
                              size="sm"
                              color="primary"
                              variant="light"
                              onPress={() => openEditModal(prescription.exerciseId)}
                              disabled={revokingExerciseId !== null || isUpdating || isReordering}
                              title="Edit prescription"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              isIconOnly
                              size="sm"
                              color="danger"
                              variant="light"
                              onPress={() => openRevokeConfirmModal(prescription.exerciseId)}
                              isLoading={revokingExerciseId === prescription.exerciseId}
                              disabled={revokingExerciseId !== null || isUpdating || isReordering}
                              title="Remove prescription"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400 mb-2">No prescriptions assigned</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Click "Add Prescription" to assign exercises to this user
                  </p>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* System Information */}
        <Card className="bg-white dark:bg-gray-800 shadow-sm">
          <CardBody className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <ClockIcon className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                System Information
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Created</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDate(user.created_at)}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Last Updated</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDate(user.updated_at)}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Exercise Statistics (Placeholder for future implementation) */}
        <Card className="bg-white dark:bg-gray-800 shadow-sm">
          <CardBody className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <HeartIcon className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Exercise Statistics
              </h3>
            </div>
            
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400 mb-2">
                Exercise tracking will be available soon
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                This section will show exercise history, progress, and performance metrics
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
        disabled={isUploading}
      />

      {/* Add Prescription Modal */}
      <Modal 
        isOpen={isPrescriptionModalOpen} 
        onOpenChange={setIsPrescriptionModalOpen}
        size="2xl"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold">Add Exercise Prescription</h3>
                <p className="text-sm text-gray-500">
                  Assign an exercise to {user?.displayName || user?.name || 'this user'}
                </p>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  {/* Exercise Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Exercise
                    </label>
                    <Select
                      placeholder="Choose an exercise"
                      selectedKeys={selectedExerciseId ? [selectedExerciseId] : []}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0];
                        setSelectedExerciseId(selected);
                      }}
                    >
                      {exercises.map((exercise) => (
                        <SelectItem key={exercise.id} value={exercise.id}>
                          {exercise.title || `Exercise ${exercise.id}`}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>

                  {/* Repetition Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Repetition
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter number of repetitions"
                      value={prescriptionForm.repetition}
                      onChange={(e) => handlePrescriptionFormChange('repetition', e.target.value)}
                      min="1"
                      max="100"
                    />
                  </div>

                  {/* Hold Time Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hold Time (seconds)
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter hold time in seconds"
                      value={prescriptionForm.holdTime}
                      onChange={(e) => handlePrescriptionFormChange('holdTime', e.target.value)}
                      min="1"
                      max="300"
                    />
                  </div>

                  {/* Sets Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sets
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter number of sets"
                      value={prescriptionForm.sets}
                      onChange={(e) => handlePrescriptionFormChange('sets', e.target.value)}
                      min="1"
                      max="20"
                    />
                  </div>

                  {/* Rest Duration Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rest Duration (seconds)
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter rest duration in seconds"
                      value={prescriptionForm.restDuration}
                      onChange={(e) => handlePrescriptionFormChange('restDuration', e.target.value)}
                      min="0"
                      max="300"
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button 
                  color="danger" 
                  variant="light" 
                  onPress={onClose}
                  disabled={isPrescribing}
                >
                  Cancel
                </Button>
                <Button 
                  color="primary" 
                  onPress={handleAddPrescription}
                  isLoading={isPrescribing}
                  disabled={!selectedExerciseId || !prescriptionForm.repetition || !prescriptionForm.holdTime || prescriptionForm.sets === '' || prescriptionForm.restDuration === ''}
                >
                  {isPrescribing ? 'Adding...' : 'Add Prescription'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Edit Prescription Modal */}
      <Modal 
        isOpen={isEditModalOpen} 
        onOpenChange={setIsEditModalOpen}
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold">Edit Exercise Prescription</h3>
                <p className="text-sm text-gray-500">
                  Update prescription for Exercise ID {editingExerciseId}
                </p>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  {/* Exercise ID Display (Read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Exercise ID
                    </label>
                    <Input
                      value={editingExerciseId || ''}
                      isReadOnly
                      variant="bordered"
                      description="Exercise ID cannot be changed"
                    />
                  </div>

                  {/* Repetition Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Repetition
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter number of repetitions"
                      value={prescriptionForm.repetition}
                      onChange={(e) => handlePrescriptionFormChange('repetition', e.target.value)}
                      min="1"
                      max="100"
                    />
                  </div>

                  {/* Hold Time Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hold Time (seconds)
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter hold time in seconds"
                      value={prescriptionForm.holdTime}
                      onChange={(e) => handlePrescriptionFormChange('holdTime', e.target.value)}
                      min="1"
                      max="300"
                    />
                  </div>

                  {/* Sets Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sets
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter number of sets"
                      value={prescriptionForm.sets}
                      onChange={(e) => handlePrescriptionFormChange('sets', e.target.value)}
                      min="1"
                      max="20"
                    />
                  </div>

                  {/* Rest Duration Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rest Duration (seconds)
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter rest duration in seconds"
                      value={prescriptionForm.restDuration}
                      onChange={(e) => handlePrescriptionFormChange('restDuration', e.target.value)}
                      min="0"
                      max="300"
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button 
                  color="danger" 
                  variant="light" 
                  onPress={onClose}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button 
                  color="primary" 
                  onPress={handleEditPrescription}
                  isLoading={isUpdating}
                  disabled={!prescriptionForm.repetition || !prescriptionForm.holdTime || prescriptionForm.sets === '' || prescriptionForm.restDuration === ''}
                >
                  {isUpdating ? 'Updating...' : 'Update Prescription'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Draggable Video Preview Panel */}
      {videoPanel1 && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{
            width: 'min(688px, 72vw)',
            left: `max(20px, calc(50% - 344px + ${videoPanel1Offset.x}px))`,
            top: `max(20px, calc(50% - 220px + ${videoPanel1Offset.y}px))`,
          }}
        >
          {/* Header - drag handle */}
          <div
            className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 cursor-move select-none bg-gray-50 dark:bg-gray-800"
            onMouseDown={(e) => startDraggingModal(e, videoDragRef, videoPanel1Offset, setVideoPanel1Offset)}
            onDoubleClick={() => resetModalDragPosition(setVideoPanel1Offset)}
          >
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Exercise Video Preview</h3>
              <p className="text-sm text-gray-500">
                {videoPanel1.title} (ID: {videoPanel1.exerciseId})
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                <Bars3Icon className="h-3 w-3" />
                Drag
              </span>
              <button
                className="rounded-full p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => { setVideoPanel1(null); setVideoPanel2(null); }}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          {/* Body */}
          <div className="px-6 pt-4 pb-4">
            {videoPanel1.previewVideoPath ? (
              <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                <video
                  key={videoPanel1.previewVideoPath}
                  controls
                  preload="metadata"
                  className="w-full h-full object-contain"
                >
                  <source src={videoPanel1.previewVideoPath} type="video/mp4" />
                  <source src={videoPanel1.previewVideoPath} type="video/webm" />
                  <source src={videoPanel1.previewVideoPath} type="video/ogg" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Video preview is not available for this exercise yet.
                </p>
              </div>
            )}
          </div>
          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 pb-4">
            <Button
              color="primary"
              onPress={() => { setVideoPanel1(null); setVideoPanel2(null); }}
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Draggable Video Preview Panel 2 */}
      {videoPanel2 && (
        <div
          className="fixed z-[60] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-blue-300 dark:border-blue-600 overflow-hidden"
          style={{
            width: 'min(688px, 72vw)',
            left: `max(20px, calc(50% - 344px + ${videoPanel2Offset.x}px))`,
            top: `max(20px, calc(50% - 220px + ${videoPanel2Offset.y}px))`,
          }}
        >
          <div
            className="flex items-center justify-between px-6 py-4 border-b border-blue-200 dark:border-blue-700 cursor-move select-none bg-blue-50 dark:bg-blue-900/30"
            onMouseDown={(e) => startDraggingModal(e, videoInfoDragRef, videoPanel2Offset, setVideoPanel2Offset)}
            onDoubleClick={() => resetModalDragPosition(setVideoPanel2Offset)}
          >
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Exercise Video Preview</h3>
              <p className="text-sm text-gray-500">
                {videoPanel2.title} (ID: {videoPanel2.exerciseId})
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                <Bars3Icon className="h-3 w-3" />
                Drag
              </span>
              <button
                className="rounded-full p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => setVideoPanel2(null)}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="px-6 pt-4 pb-4">
            {videoPanel2.previewVideoPath ? (
              <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                <video
                  key={videoPanel2.previewVideoPath}
                  controls
                  preload="metadata"
                  className="w-full h-full object-contain"
                >
                  <source src={videoPanel2.previewVideoPath} type="video/mp4" />
                  <source src={videoPanel2.previewVideoPath} type="video/webm" />
                  <source src={videoPanel2.previewVideoPath} type="video/ogg" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Video preview is not available for this exercise yet.
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end px-6 pb-4">
            <Button color="primary" onPress={() => setVideoPanel2(null)}>Close</Button>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Revoke Prescription */}
      <Modal 
        isOpen={isConfirmModalOpen} 
        onOpenChange={setIsConfirmModalOpen}
        size="sm"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold text-red-600">Remove Prescription</h3>
                <p className="text-sm text-gray-500">
                  Are you sure you want to remove this exercise prescription?
                </p>
              </ModalHeader>
              <ModalBody>
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    This action cannot be undone. The exercise will be removed from the user's prescription list.
                  </p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button 
                  color="default" 
                  variant="light" 
                  onPress={onClose}
                  disabled={revokingExerciseId !== null}
                >
                  Cancel
                </Button>
                <Button 
                  color="danger" 
                  onPress={handleRevokePrescription}
                  isLoading={revokingExerciseId !== null}
                >
                  {revokingExerciseId !== null ? 'Removing...' : 'Remove Prescription'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default UserDetail;
